import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ALL_SERVICES, SERVICE_DETAILS } from "../lib/data";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import FAQAccordion from "../components/FAQAccordion";
import Calculators from "../components/Calculators";

export function ServicesHub() {
  const { lang } = useApp();
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "services.hubLabel")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-2">{t(lang, "services.hubTitle")}</h1>
      <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-3xl leading-relaxed">{t(lang, "services.hubSub")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {ALL_SERVICES.filter(s => !s.hidden).map((s) => {
          const d = SERVICE_DETAILS[s.slug];
          const localizedTitle = t(lang, `services.titles.${s.slug}`) || s.title;
          return (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`hub-service-${s.slug}`}>
              <Card className="card-lift h-full">
                <CardContent className="p-7">
                  <h3 className="font-serif text-xl">{localizedTitle}</h3>
                  {d?.tagline && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{d.tagline}</p>}
                  <span className="inline-flex items-center gap-1 text-sm text-primary mt-4 font-medium">{t(lang, "common.learnMore")} <ArrowRight className="h-3.5 w-3.5" /></span>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const { lang } = useApp();
  const d = SERVICE_DETAILS[slug];

  if (!d) return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center">
      <h1 className="font-serif text-4xl">Service not found</h1>
      <Link to="/services" className="text-primary hover:underline mt-4 inline-block">← {t(lang, "services.hubLabel")}</Link>
    </div>
  );

  const localizedTitle = t(lang, `services.titles.${slug}`) || d.title;

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/services" className="text-sm text-muted-foreground hover:text-primary" data-testid="back-to-services">← {t(lang, "services.hubLabel")}</Link>
        <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary mt-4">{d.tagline}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-2 max-w-4xl">{localizedTitle}</h1>
        <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-3xl leading-relaxed">{d.intro}</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <h2 className="font-serif text-2xl sm:text-3xl mb-6">{t(lang, "sections.offerings")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {d.benefits.map((b) => (
            <div key={b} className="flex items-start gap-3 p-5 rounded-lg border border-border bg-card">
              <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5 shrink-0" />
              <span className="text-sm leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {d.categories && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <h2 className="font-serif text-2xl sm:text-3xl mb-6">{t(lang, "sections.categories")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {d.categories.map((g) => (
              <div key={g.group} className="rounded-xl border border-border p-6 bg-card">
                <div className="font-serif text-lg">{g.group}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {g.items.map((it) => (
                    <span key={it} className="text-xs px-3 py-1.5 rounded-full bg-muted">{it}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {d.calculators?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-serif text-2xl sm:text-3xl mb-6">{t(lang, "sections.tools")}</h2>
          <Calculators keys={d.calculators} />
        </section>
      )}

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.faqsLong")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl mt-2 mb-8">{t(lang, "sections.faqs")}</h2>
        <FAQAccordion faqs={d.faqs} testIdPrefix={`faq-${slug}`} />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-card border border-border p-10 flex flex-col md:flex-row gap-6 items-center justify-between">
          <div>
            <h3 className="font-serif text-2xl">Want personal guidance on {localizedTitle}?</h3>
            <p className="text-sm text-muted-foreground mt-2">Speak with our wealth team — no obligation.</p>
          </div>
          <Link to="/contact"><Button data-testid="service-cta-contact" size="lg" className="bg-primary text-primary-foreground hover:brightness-110">{t(lang, "cta.book")}</Button></Link>
        </div>
        <p className="text-xs italic text-muted-foreground mt-6">{t(lang, "footer.risk")}</p>
      </section>
    </div>
  );
}
